import type { TFunction } from 'i18next';
import type { AudienceForm, GroupLike } from '../types.ts';
import { filterNonEmptyGroups } from './filterNonEmptyGroups.ts';
import { getAnyIncludesMarketRestrictionReason } from './marketOperatorRestrictions.ts';

export type ValidationError = {
  path: string;
  message: string;
};

export type ValidationResult = {
  isValid: boolean;
  error?: ValidationError;
};

export const hasAnySegments = (groups: GroupLike[]): boolean => {
  for (const group of groups) {
    if ('groups' in group && group.groups) {
      const nonEmptySubgroups = filterNonEmptyGroups(group.groups);

      if (nonEmptySubgroups.length > 0) {
        return true;
      }
    } else {
      const nonEmptyGroup = filterNonEmptyGroups([group]);

      if (nonEmptyGroup.length > 0) {
        return true;
      }
    }
  }
  return false;
};

export type ValidateNormalizedDefinitionContext = {
  accountId?: string;
  shouldUseRegionFirstFlow?: boolean;
};

export const validateNormalizedDefinition = (
  normalizedDefinition: AudienceForm['normalized_definition'] | undefined,
  t: TFunction,
  context?: ValidateNormalizedDefinitionContext
): ValidationResult => {
  if (!normalizedDefinition) {
    return { isValid: true };
  }

  const includesGroups = normalizedDefinition.includes?.groups || [];
  const excludesGroups = normalizedDefinition.excludes?.groups || [];

  if (hasAnySegments(excludesGroups) && !hasAnySegments(includesGroups)) {
    return {
      isValid: false,
      error: {
        path: 'normalized_definition.excludes.groups',
        message: t(
          'You cannot add exclusions without first defining what to include. Please add segments to your main audience groups.'
        ),
      },
    };
  }

  const accountId = context?.accountId ?? '';
  const includesMarketBlocksExcludes =
    getAnyIncludesMarketRestrictionReason(
      includesGroups,
      accountId,
      context?.shouldUseRegionFirstFlow ?? false,
      true
    ) != null;

  if (includesMarketBlocksExcludes && hasAnySegments(excludesGroups)) {
    return {
      isValid: false,
      error: {
        path: 'normalized_definition.excludes.groups',
        message: t(
          'Exclusions are not available while your include groups use cross-region targeting or sellable multipartner (Data Marketplace) segments on non-US markets. Remove exclusions or adjust include groups, then save again.'
        ),
      },
    };
  }

  if (!hasAnySegments(includesGroups)) {
    return {
      isValid: false,
      error: {
        path: 'normalized_definition.includes.groups',
        message: t(
          'Your audience must include at least one segment. Please add segments to define your target audience.'
        ),
      },
    };
  }

  return { isValid: true };
};
